import { action } from 'typesafe-actions';
import * as actionTypes from 'constants/action-types';
import {
  TEMPLATE_UPDATE_SHOW_PRIORITY_MODAL,
  TEMPLATE_UPDATE_SHUTTLE_ACTIVE_PAGE,
  TEMPLATE_UPDATE_SHUTTLE_MOVE_IN_PROGRESS,
  TEMPLATE_UPDATE_SHUTTLE_SORT,
  TEMPLATE_UPDATE_SHUTTLE_TRANSACTION,
  TEMPLATE_UPDATE_SHUTTLE_TRANSACTION_SELECTION,
} from 'constants/action-types';
import { MockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplateTypes';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { ApplicationState } from 'store/rootReducer';
import { MockServiceActionTypes } from 'store/mockServices/mockServiceActionTypes';
import { PanePosition, ShuttlePaneControls } from 'store/transactionShuttle/TransactionShuttleTypes';
import axios, { AxiosResponse, CancelToken } from 'axios';
import { BlazeMeterApiResponse } from 'types/rest/blazeMeterApiResponse';
import axiosInstance from 'axios/axios-base';
import { cacheControlHeaders, getErrorMessage, isCancelTokenErrorMessage } from 'utils/miscUtil';
import { AnyAction } from 'redux';
import _ from 'lodash';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import {
  createTemplateDraft,
  getExpandedTemplate,
  setIsSaving,
  swapMockServiceTemplateAction,
  updateMockServiceTemplate,
  updateMockServiceTemplateAction,
} from 'store/mockServiceTemplates/mockServiceTemplatesActions';
import { MockServiceTemplateDetailState } from './mockServiceTemplateDetailReducer';
import { Recording, Transaction, TransactionType } from 'types/rest/assetCatalogTypes';
import { SelectAllType } from '../../../components/blazeTable/BlazeTable.types';
import acApi from '../../../axios/ac/acApi';
import { MsDetailParametersTab } from '../../mockServices/mockServiceTypes';
import { AssignmentActionType } from '../../../components/transactionsShuttle/TransactionShuttle.types';
import { buildSortingParam } from '../../../utils/pagebleUtil';
import { SortConfiguration } from '../../../types/rest/paginationTypes';
import { AssetAssignmentBundle } from '../../../views/MockServices/Tabs/MockServices/SslConfiguration';
import { RunnerTabType } from '../../mockServices/mockServiceDetail/mockServiceDetailTypes';
import { prepareRecordingPatch } from '../../mockServices/mockServiceDetail/mockServiceDetailActions';
import { PaneState } from '../../../components/shuttle/Shuttle';

/**
 * Set active tab in template detail
 */
export const updateActiveTab = (activeTab: string) => action(actionTypes.TEMPLATE_DETAIL_UPDATE_ACTIVE_TAB, activeTab);

export const updateTemplateActiveParamTab = (activeTab: MsDetailParametersTab) =>
  action(actionTypes.TEMPLATE_DETAIL_UPDATE_ACTIVE_PARAM_TAB, activeTab);

export const setTemplateAssetBundle = (assetBundle: AssetAssignmentBundle) =>
  action(actionTypes.TEMPLATE_DETAIL_SET_ASSET_BUNDLE, assetBundle);

/**
 * Sets active page in one of the shuttle panes.
 */
export const updateTemplateShuttleActivePage = (type: PanePosition, activePage: number) => {
  return action(TEMPLATE_UPDATE_SHUTTLE_ACTIVE_PAGE, {
    type: type,
    activePage: activePage,
  });
};

export const updateTemplateShuttleSort = (type: PanePosition, sort: SortConfiguration) => {
  return action(TEMPLATE_UPDATE_SHUTTLE_SORT, {
    type: type,
    sort: sort,
  });
};

export const updateTemplateShowPriorityModal = (showPriorityModal: boolean) => {
  return action(TEMPLATE_UPDATE_SHOW_PRIORITY_MODAL, showPriorityModal);
};

/**
 * Update transactions in the template shuttle
 * @param type - LEFT or RIGHT
 * @param totalResults - total number of transactions in the backend
 * @param transactions - transactions to update
 */
export const getTransactionsForTemplateShuttleAction = (
  type: PanePosition,
  totalResults: number,
  transactions: Transaction[],
) =>
  action(actionTypes.TEMPLATE_UPDATE_SHUTTLE_TRANSACTIONS, {
    type: type,
    totalResults: totalResults,
    transactions: transactions,
  });

/**
 * Update search term in one of the panes.
 * @param type
 * @param searchTerm
 */
export const updateTemplateShuttleSearchTerm = (type: PanePosition, searchTerm: string) =>
  action(actionTypes.TEMPLATE_UPDATE_SHUTTLE_SEARCH_TERM, {
    type: type,
    searchTerm: searchTerm,
  });

export const updateTemplateRunnerTab = (activeRunnerTab: RunnerTabType) =>
  action(actionTypes.TEMPLATE_UPDATE_RUNNER_TAB, activeRunnerTab);

export const updateTemplateHttpRunnerTab = (activeTab: string) =>
  action(actionTypes.TEMPLATE_HTTP_RUNNER_UPDATE_TAB, activeTab);

export const updateTemplateMessagingRunnerTab = (activeTab: string) =>
  action(actionTypes.TEMPLATE_MESSAGING_RUNNER_UPDATE_TAB, activeTab);

export const updateTemplateMessagingConfigTab = (activeTab: string) =>
  action(actionTypes.TEMPLATE_MSG_CONFIG_SET_ACTIVE_TAB, activeTab);

/**
 * Sets template shuttle to loading state.
 * @param type - ADD or REMOVE
 * @param isInProgress - true if loading, false otherwise
 */
export const templateShuttleMoveInProgressAction = (type: AssignmentActionType, isInProgress: boolean) => {
  return action(TEMPLATE_UPDATE_SHUTTLE_MOVE_IN_PROGRESS, {
    type: type,
    inProgress: isInProgress,
  });
};

/**
 * Updates single transaction in the shuttle
 */
export const updateTemplateShuttleTransaction = (type: PanePosition, transaction: Transaction) => {
  return action(TEMPLATE_UPDATE_SHUTTLE_TRANSACTION, {
    type: type,
    transaction: transaction,
  });
};

/**
 * Updates transaction selection
 */
export const updateTemplateShuttleTransactionSelection = (
  type: PanePosition,
  transactions: number[],
  selectAll?: SelectAllType,
) => {
  return action(TEMPLATE_UPDATE_SHUTTLE_TRANSACTION_SELECTION, {
    type: type,
    transactions: transactions,
    selectAll: selectAll,
  });
};

/**
 * Template pane is loading
 */
export const templatePaneIsLoadingAction = (paneType: PanePosition, isLoading: boolean) =>
  action(actionTypes.TEMPLATE_DETAIL_PANE_IS_LOADING, {
    paneType: paneType,
    isLoading: isLoading,
  });

/**
 * Expand row in the transaction shuttle.
 */
export const expandTemplateTransactionAction = (paneType: PanePosition, expandItemId: number | null) =>
  action(actionTypes.TEMPLATE_SHUTTLE_EXPAND_ITEM, {
    paneType: paneType,
    expandItemId: expandItemId,
  });

export const getActiveTemplateRunner = (getState: any) => {
  return getState().mockServiceTemplateDetail.runnerTab;
};

export const updateTemplateRecordingShuttlePane = (position: PanePosition, state: PaneState) =>
  action(actionTypes.TEMPLATE_RECORDING_SHUTTLE_UPDATE_PANE, {
    position: position,
    state: state,
  });

/**
 * Reloads both right pane and left pane transactions.
 * @param template - template to reload transactions for
 * @param transactionType (HTTP, JMS, etc.)
 */
export const reloadTransactionsForTemplate = (
  template: MockServiceTemplate,
  transactionType: TransactionType,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: any) => {
    dispatch(fetchTransactionsForTemplate(template, 'LEFT', transactionType));
    dispatch(fetchTransactionsForTemplate(template, 'RIGHT', transactionType));
  };
};

/**
 * Discards all local changes made to the template.
 * @param template
 */
export const discardLocalTemplateChanges = (
  template: MockServiceTemplate,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: any, getState: any) => {
    if (template.isDraft !== true) {
      dispatch(showToastNotification(ToastType.Danger, 'Cannot discard template. Not a draft'));
    }
    try {
      dispatch(setIsSaving(true));
      await axiosInstance.delete(`/workspaces/{workspaceId}/service-mock-template-drafts/${template.id}`);

      // check, if there was also original attached to this draft
      if (template.serviceMockTemplateId !== undefined) {
        const original = (
          await axiosInstance.get(`/workspaces/{workspaceId}/service-mock-templates/${template.serviceMockTemplateId}`)
        ).data.result;
        original.isDraft = false;
        dispatch(swapMockServiceTemplateAction(template.id, original));
        dispatch(
          showToastNotification(
            ToastType.Success,
            'Local Changes Revereted',
            'Your local changes have been successfully reverted.',
          ),
        );

        //Reload Assets for template after changes are discarded
        //Check which runner is active and reload shuttle
        const runner = getActiveTemplateRunner(getState);
        const transactionType = runner === 'httpRunner' ? 'HTTP' : 'MESSAGING';
        dispatch(reloadTransactionsForTemplate(original, transactionType));
        dispatch(getAssetsForTemplate(original));
      } else {
        // If it does not have any original, just delete the object from redux
        dispatch({
          type: actionTypes.MOCK_SERVICE_TEMPLATES_DELETE,
          payload: template,
        });
        dispatch(showToastNotification(ToastType.Success, 'Draft has been successfully deleted.'));
      }
    } catch (error) {
      dispatch(showToastNotification(ToastType.Danger, 'Cannot Discard Local changes'));
    } finally {
      dispatch(setIsSaving(false));
    }
  };
};

/**
 * Reload transactions for shuttle.
 * @param template - template to reload transactions for
 * @param paneType (LEFT, RIGHT)
 * @param transactionType (HTTP, JMS, etc.)
 */
export const fetchTransactionsForTemplate = (
  template: MockServiceTemplate,
  paneType: PanePosition,
  transactionType: TransactionType,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: any, getState: any) => {
    if (!transactionType) {
      throw Error('Transaction type is required');
    }
    // Show loading indicator
    dispatch(templatePaneIsLoadingAction(paneType, true));

    //API differs for left / right pane
    const transactionsApi: string = resolveTemplateApi(template, paneType);

    //Get data from store
    const shuttleState = getState().mockServiceTemplateDetail.shuttle;
    const paneControls: ShuttlePaneControls =
      paneType === 'LEFT' ? { ...shuttleState.leftPane } : { ...shuttleState.rightPane };
    const params = buildParams(paneControls, template, paneControls.sort, transactionType);
    const token = buildCancelToken(paneType);

    //Get configuration from store
    try {
      const responseData: BlazeMeterApiResponse = (
        await axiosInstance.get(transactionsApi, {
          params: params,
          headers: cacheControlHeaders,
          cancelToken: token,
        })
      ).data;
      const transactions = responseData.result || [];
      const totalResults = responseData.total as number;

      // Update pane controls
      dispatch(getTransactionsForTemplateShuttleAction(paneType, totalResults, transactions));

      // Go to the first page if necessary (when there are some results in the backend db,
      // but we do not have any on the current page)
      let activePage = paneControls.activePage;
      if (activePage > 1 && totalResults >= paneControls.pageSize && transactions.length === 0) {
        dispatch(updateTemplateShuttleActivePage(paneType, --activePage));
      }
    } catch (error) {
      console.error('Transaction fetch failed', error);
      //Ignore cancellation error
      const msg = getErrorMessage(error);
      if (!isCancelTokenErrorMessage(msg)) {
        dispatch(showToastNotification(ToastType.Danger, 'Cannot get list of transactions for template', msg));
      }
    } finally {
      dispatch(templatePaneIsLoadingAction(paneType, false));
    }
  };
};

const isSelectAllInPaneActive = (mockServiceDetailState: MockServiceTemplateDetailState, type: PanePosition) => {
  const paneSelector = type == 'LEFT' ? 'leftPane' : 'rightPane';
  return mockServiceDetailState.shuttle[paneSelector].selectAll == 'ALL';
};

/**
 * Changes transaction assignment for template.
 * @param template - template to move transactions for
 * @param assignmentActionType - ADD or REMOVE
 * @param singleTx - optional, if provided, only this transaction will be moved
 * @param flowConfig - optional, flow configuration to assign to transactions
 */
export const moveTemplateTransactions: any = (
  template: MockServiceTemplate,
  assignmentActionType: AssignmentActionType,
  singleTx?: number,
  flowConfig?: string,
): ThunkAction<any, ApplicationState, null, any> => {
  return async (dispatch: ThunkDispatch<any, any, AnyAction>, getState: any) => {
    const mockServiceDetailState: MockServiceTemplateDetailState = _.cloneDeep(
      getState().mockServiceTemplateDetail as MockServiceTemplateDetailState,
    );

    // Show inProgress indicator
    await dispatch(templateShuttleMoveInProgressAction(assignmentActionType, true));

    // Assign in backend
    let patchBody;
    let searchTerm;

    if (assignmentActionType === AssignmentActionType.ADD) {
      const isSelectAllActive = isSelectAllInPaneActive(mockServiceDetailState, 'LEFT');
      const transactionIds = isSelectAllActive ? [-1] : mockServiceDetailState.shuttle.leftPane.selected;
      patchBody = {
        includeIds: singleTx === undefined ? transactionIds : [singleTx],
        flowConfiguration: flowConfig,
      };

      if (mockServiceDetailState.shuttle.leftPane.searchTerm) {
        searchTerm = mockServiceDetailState.shuttle.leftPane.searchTerm;
      }
    } else {
      const isSelectAllActive = isSelectAllInPaneActive(mockServiceDetailState, 'RIGHT');
      const transactionIds = isSelectAllActive ? [-1] : mockServiceDetailState.shuttle.rightPane.selected;
      patchBody = {
        excludeIds: singleTx === undefined ? transactionIds : [singleTx],
      };

      if (mockServiceDetailState.shuttle.rightPane.searchTerm) {
        searchTerm = mockServiceDetailState.shuttle.rightPane.searchTerm;
      }
    }

    // If a search term is available, append it to patch body
    if (!_.isNil(searchTerm)) {
      patchBody = { ...patchBody, filter: searchTerm };
    }

    //If it is not a draft, created it beforehand
    if (!template.isDraft) {
      const draft = await dispatch(createTemplateDraft(template));
      const oldTemplateId = template.id;

      // Rewrite template with draft returned from backend
      template.id = draft.id;
      template.isDraft = true;
      template.serviceMockTemplateId = oldTemplateId;

      // If draft creation failed
      if (!draft) {
        return;
      }

      dispatch(swapMockServiceTemplateAction(oldTemplateId, template));
    } else {
      await dispatch(updateMockServiceTemplateAction(template));
    }
    const currentTemplate = { ...template };
    const patchUrl = '/workspaces/{workspaceId}/service-mock-template-drafts/';
    try {
      const data: BlazeMeterApiResponse = (await axiosInstance.patch(patchUrl + template.id, patchBody)).data;
      currentTemplate.mockServiceTransactions = data.result.mockServiceTransactions;
    } catch (error) {
      const msg = _.get(error, 'data.result.error', '');
      dispatch(showToastNotification(ToastType.Danger, 'Cannot assign transaction', msg));
    } finally {
      await dispatch(templateShuttleMoveInProgressAction(assignmentActionType, false));
      await dispatch(updateMockServiceTemplateAction(currentTemplate));

      //After moving is done, reset the current selection in panes and reload the shuttle
      await dispatch(updateTemplateShuttleTransactionSelection('LEFT', [], undefined));
      await dispatch(updateTemplateShuttleTransactionSelection('RIGHT', [], undefined));

      //Check which runner is active and reload shuttle
      const runner = getActiveTemplateRunner(getState);
      const transactionType = runner === 'httpRunner' ? 'HTTP' : 'MESSAGING';
      await dispatch(reloadTransactionsForTemplate(currentTemplate, transactionType));
    }
  };
};

let cancelTokenSourceLeftPane: any;
let cancelTokenSourceRightPane: any;
export const buildCancelToken = (paneType: PanePosition) => {
  let token: CancelToken | undefined;
  if (paneType === 'LEFT') {
    // Cancel previous request for left pane - if any pending
    if (cancelTokenSourceLeftPane) {
      cancelTokenSourceLeftPane.cancel('Fetching Transactions cancelled for ' + paneType);
    }

    cancelTokenSourceLeftPane = axios.CancelToken.source();
    token = cancelTokenSourceLeftPane.token;
  } else if (paneType === 'RIGHT') {
    // Cancel previous request for right pane - if any pending
    if (cancelTokenSourceRightPane) {
      cancelTokenSourceRightPane.cancel('Fetching Transactions cancelled for ' + paneType);
    }
    cancelTokenSourceRightPane = axios.CancelToken.source();
    token = cancelTokenSourceRightPane.token;
  }
  return token;
};

/**
 * Builds api query parameters based on current ui state (pagination, filtering etc.).
 * @param paneControls - controls for left or right pane
 * @param template - template to get transactions for
 * @param sort - sorting configuration
 * @param transactionType - type of transaction (HTTP, JMS, etc.)
 */
export const buildParams = (
  paneControls: ShuttlePaneControls,
  template: MockServiceTemplate,
  sort?: SortConfiguration,
  transactionType?: TransactionType,
) => {
  const params: any = {
    skip: paneControls.pageSize * (paneControls.activePage - 1),
    limit: paneControls.pageSize,
    serviceId: template.serviceId,
    sort: _.isNil(sort) ? '-id' : buildSortingParam(sort),
    filter: paneControls.searchTerm ? paneControls.searchTerm : null,
    type: transactionType,
  };
  return params;
};

/**
 * Resets detail view to initial state
 */
export const resetTemplateDetailView = () => action(actionTypes.TEMPLATE_DETAIL_RESET_VIEW);

/**
 * Resets Transaction Shuttle
 */
export const resetTemplateTransactionShuttle = () => action(actionTypes.RESET_TRANSACTION_SHUTTLE);

export const updateTemplateRecordingShuttlePaneInProgress = (progress: {
  isMoveLeftInProgress?: boolean;
  isMoveRightInProgress?: boolean;
}) => action(actionTypes.TEMPLATE_RECORDING_SHUTTLE_UPDATE_IN_PROGRESS, progress);

/**
 * Fetches recordings for the template.
 * @param template
 * @param resetSelection
 */
export const fetchAssignedRecordingsForTemplate = (template: MockServiceTemplate, resetSelection?: boolean) => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    const rightPane = _.cloneDeep(getState().mockServiceTemplateDetail.recordingShuttle.rightPane);

    //Set Is Loading
    dispatch(updateTemplateRecordingShuttlePane('RIGHT', { ...rightPane, isLoading: true }));

    const params: any = buildParams(rightPane, template);
    let response = null;

    try {
      if (template.isDraft) {
        response = (await acApi.templateDrafts.getRecordings(template.id, params)).data;
      } else {
        response = (await acApi.templates.getRecordings(template.id, params)).data;
      }

      rightPane.totalResults = response.total;
      rightPane.data = response.result;
      rightPane.isLoading = false;
      rightPane.selected = resetSelection ? [] : rightPane.selected;
      dispatch(updateTemplateRecordingShuttlePane('RIGHT', rightPane));
    } catch (error) {
      const message = getErrorMessage(error);
      dispatch(showToastNotification(ToastType.Danger, 'Cannot get list of recordings', message));
      dispatch(updateTemplateRecordingShuttlePane('RIGHT', { ...rightPane, isLoading: false }));
    }
  };
};

/**
 * Moves recordings between panes.
 * @param position - LEFT or RIGHT
 * @param template - template to move recordings for
 * @param recordingIds - ids of recordings to move
 */
export const moveTemplateRecordingsThunk = (
  position: PanePosition,
  template: MockServiceTemplate,
  recordingIds: number[],
) => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    //From Right Pane we are moving left
    if (position === 'RIGHT') {
      dispatch(updateTemplateRecordingShuttlePaneInProgress({ isMoveLeftInProgress: true }));
    } else {
      dispatch(updateTemplateRecordingShuttlePaneInProgress({ isMoveRightInProgress: true }));
    }

    //If it is not a draft, create it beforehand
    if (!template.isDraft) {
      const draft = await dispatch(createTemplateDraft(template));
      template.id = draft.id;
      template.isDraft = true;
    }

    try {
      const patch = prepareRecordingPatch(position, recordingIds);
      await acApi.templateDrafts.patchDraftById(template.id, patch);

      //Refresh panes after the move
      dispatch(fetchAvailableRecordingsForTemplate(template, true));
      dispatch(fetchAssignedRecordingsForTemplate(template, true));
    } catch (e) {
      const message = getErrorMessage(e);
      dispatch(showToastNotification(ToastType.Danger, 'Cannot assign / de-assign recordings', message));
    } finally {
      //No progress is shown
      dispatch(
        updateTemplateRecordingShuttlePaneInProgress({ isMoveLeftInProgress: false, isMoveRightInProgress: false }),
      );
    }
  };
};

/**
 * Fetches available recordings which can be assigned to the template.
 * @param template - template to fetch recordings for
 * @param resetSelection - if true, resets selection in the pane
 */
export const fetchAvailableRecordingsForTemplate = (template: MockServiceTemplate, resetSelection?: boolean) => {
  return async (dispatch: ThunkDispatch<any, any, AnyAction>, getState: any) => {
    const leftPane = _.cloneDeep(getState().mockServiceTemplateDetail.recordingShuttle.leftPane);

    //Set Is Loading
    dispatch(updateTemplateRecordingShuttlePane('LEFT', { ...leftPane, isLoading: true }));

    let params: any = buildParams(leftPane, template);
    if (!template.isDraft) {
      params = { ...params, excludeTemplateId: template.id, serviceId: template.serviceId };
    } else {
      params = { ...params, excludeTemplateDraftId: template.id, serviceId: template.serviceId };
    }

    try {
      const response = (await acApi.recordings.getRecordings(params)).data;
      leftPane.totalResults = response.total;
      leftPane.data = response.result;
      leftPane.isLoading = false;
      leftPane.selected = resetSelection ? [] : leftPane.selected;
      dispatch(updateTemplateRecordingShuttlePane('LEFT', leftPane));
    } catch (error) {
      const message = getErrorMessage(error);
      dispatch(showToastNotification(ToastType.Danger, 'Cannot get list of recordings', message));
      dispatch(updateTemplateRecordingShuttlePane('LEFT', { ...leftPane, isLoading: false }));
    }
  };
};

/**
 * Resolves api for left / right pane.
 * The Left pane contains all transactions within a particular service.
 * The Right pane contains
 * transactions which are assigned to a particular virtual service template.
 * @param template
 * @param type
 */
export const resolveTemplateApi = (template: MockServiceTemplate, type: PanePosition) => {
  let leftPaneApi = `/workspaces/{workspaceId}/transactions?`;
  let rightPaneApi;

  if (template.isDraft === true) {
    leftPaneApi += `excludeTemplateDraftId=${template.id}`;
    rightPaneApi = `/workspaces/{workspaceId}/service-mock-template-drafts/${template.id}/transactions/`;
  } else {
    leftPaneApi += `excludeTemplateId=${template.id}`;
    rightPaneApi = `/workspaces/{workspaceId}/service-mock-templates/${template.id}/transactions/`;
  }

  return type === 'LEFT' ? leftPaneApi : rightPaneApi;
};

export const updateTemplateAssets: any = (
  assetBundle: AssetAssignmentBundle,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    const template = _.cloneDeep(getExpandedTemplate());
    template.assets = assetBundle.assets;
    template.configKeyAssets = assetBundle.configKeyAssets;
    dispatch(updateMockServiceTemplate(template));
    dispatch(setTemplateAssetBundle(assetBundle));
  };
};

/**
 * Loads asset data for currently opened Template.
 */
export const getAssetsForTemplate: any = (
  template: MockServiceTemplate,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    let resp: AxiosResponse<any, any>;
    //Get a list of certificates
    if (template.isDraft) {
      resp = await acApi.templateDrafts.getById(template.id);
    } else {
      resp = await acApi.templates.getById(template.id);
    }
    const assets = _.get(resp, 'data.result.assets');
    const configKeyAssets = _.get(resp, 'data.result.configKeyAssets');
    dispatch(setTemplateAssetBundle({ assets: assets, configKeyAssets: configKeyAssets }));

    //Copy assets and assign them to template
    template.assets = assets;
    template.configKeyAssets = configKeyAssets;

    dispatch(updateMockServiceTemplateAction(template));
  };
};

export const updateTemplateRunTimeConfigThunk = (
  template: MockServiceTemplate,
  recordingId: number,
  runtimeConfig: any,
) => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    try {
      //Update the store
      await dispatch(updateTemplateRecordingConfig(recordingId, runtimeConfig));

      if (!template.isDraft) {
        const draft = await dispatch(createTemplateDraft(template));
        const oldTmplId = template.id;
        template.id = draft.id;
        template.isDraft = true;
        dispatch(swapMockServiceTemplateAction(oldTmplId, template));
      }

      await acApi.templateDrafts.updateRecording(template.id, recordingId, runtimeConfig);
    } catch (error) {
      const message = getErrorMessage(error);
      dispatch(showToastNotification(ToastType.Danger, 'Cannot update runtime configuration', message));
    }
  };
};

export const updateTemplateRecordingConfig = (recordingId: number, runtimeConfig: any) => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    const rightPane = (getState() as ApplicationState).mockServiceTemplateDetail.recordingShuttle.rightPane;
    const { data } = rightPane;
    const index = data.findIndex((rec: Recording) => rec.id === recordingId);
    if (index !== -1) {
      data[index].runtimeConfig = runtimeConfig;
      dispatch(updateTemplateRecordingShuttlePane('RIGHT', { ...rightPane, data: data }));
    }
  };
};
